/**
 * Cache Manager — Client-side API for the tile-caching service worker.
 *
 * Provides register, cache, clear, and size-query operations that
 * communicate with sw.js via postMessage / onmessage.
 */

/**
 * Check whether the current browser supports service workers.
 * @returns {boolean}
 */
export function isServiceWorkerSupported() {
  return 'serviceWorker' in navigator
}

/**
 * Register the service worker at ./sw.js.
 * Returns the ServiceWorkerRegistration or null if unsupported.
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  if (!isServiceWorkerSupported()) return null
  try {
    const registration = await navigator.serviceWorker.register('./sw.js')
    return registration
  } catch (err) {
    console.error('[cacheManager] SW registration failed:', err)
    return null
  }
}

/**
 * Send a message to the active service worker and wait for a typed reply.
 * @param {object} message  — payload to post
 * @param {string} replyType — the `type` field expected in the response
 * @returns {Promise<object>}
 */
function sendMessage(message, replyType) {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'))
      return
    }

    const handler = (event) => {
      if (event.data && event.data.type === replyType) {
        navigator.serviceWorker.removeEventListener('message', handler)
        resolve(event.data)
      }
    }
    navigator.serviceWorker.addEventListener('message', handler)
    navigator.serviceWorker.controller.postMessage(message)
  })
}

/**
 * Pre-cache tiles for the given geographic bounds and zoom range.
 *
 * @param {{ n: number, s: number, e: number, w: number }} bounds
 * @param {[number, number]} zoomRange — [min, max]
 * @param {function} [onProgress] — called with (loaded, total)
 * @returns {Promise<{ count: number }>}
 */
export function requestTileCache(bounds, zoomRange, onProgress) {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker || !navigator.serviceWorker.controller) {
      reject(new Error('No active service worker'))
      return
    }

    const handler = (event) => {
      if (!event.data) return
      if (event.data.type === 'CACHE_PROGRESS' && onProgress) {
        onProgress(event.data.loaded, event.data.total)
      }
      if (event.data.type === 'CACHE_COMPLETE') {
        navigator.serviceWorker.removeEventListener('message', handler)
        resolve({ count: event.data.count })
      }
    }

    navigator.serviceWorker.addEventListener('message', handler)
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_TILES',
      bounds,
      zoom: zoomRange,
    })
  })
}

/**
 * Delete all cached tiles.
 * @returns {Promise<void>}
 */
export async function clearTileCache() {
  await sendMessage({ type: 'CLEAR_CACHE' }, 'CACHE_CLEARED')
}

/**
 * Get the number of tiles currently cached.
 * @returns {Promise<number>}
 */
export async function getCacheSize() {
  const data = await sendMessage({ type: 'GET_CACHE_SIZE' }, 'CACHE_SIZE')
  return data.count
}
